import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LogoIcon } from "@/components/Icon";
import { GenUIBlock } from "@/components/GenUIBlock";
import { useChat, type ChatMessage } from "@/hooks/useChat";
import { useConversationList, type ConversationSummary } from "@/hooks/useConversationList";
import { renderMarkdown } from "@/lib/markdown";
import { apiJson, apiPostJson } from "@/lib/api";
import { STORAGE_SAVED, STORAGE_LEADS } from "@/constants/storage";
import { LEAD_SOURCE, type LeadSource } from "@/constants/leads";

/**
 * Rent app — chat-first surface (design v2).
 *
 *  - Topbar: 4 tray nav buttons (Tools / Checklist / Saved / History) +
 *    EN/中 toggle + "Switch to buy" exit + "New chat" when in a thread.
 *  - Welcome (no thread): serif hero, big composer, 6 prompt cards, 3
 *    shortcut cards, three-column footer.
 *  - Thread (after first ask): scrolling chat with you/AI bubbles, AI
 *    replies carry sources, optional inline embeds (chart / breakdown
 *    table), follow-up chips, save/copy actions. Compact composer at
 *    the bottom.
 *
 * Local mock for now — Step 5 swaps `matchReply` for a real
 * mode=consumer_rent backend stream. State (thread / saved / history)
 * is in-memory only; Step 4 surfaces them via the right-side trays.
 */

type Panel = "history" | "saved" | "tools" | "checklist" | null;

export type SavedItem = { id: string; title: string; preview: string; when: string };
export type HistoryItem = { id: string; title: string; preview: string; when: string };

export function Rent() {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const isZh = i18n.language?.startsWith("zh") ?? false;
  const [panel, setPanel] = useState<Panel>(null);

  // Live chat against the backend (mode=consumer_rent → backend picks
  // the rent-only system prompt + tool whitelist). Step 5 swap-in:
  // the previous local mock matchReply() / answer-bank is gone; replies
  // now stream from /chat/stream.
  //
  // No second arg → useChat falls back to localStorage
  // (`propwise.conversation_id.consumer_rent.v1`). This is what makes
  // refresh / re-open mid-thread restore the user's conversation
  // instead of dumping them back at the welcome screen with their
  // turn lost. Use the explicit "+ New chat" button to start clean.
  const chat = useChat("consumer_rent");

  // Local UI add-ons that survive across turns:
  //   lastBrief — the most recent parseRentBrief() hit, drives the
  //               deterministic <ListingShortlist /> rendered below
  //               the latest AI message (since the LLM has no live
  //               listings feed, we layer the URA-historical shortlist
  //               on top of its prose).
  //   saved/history — local bookmarks + thread archive used by the
  //                   right-side trays. Browser-only for now.
  const [lastBrief, setLastBrief] = useState<RentBrief | null>(null);
  // saved is persisted in localStorage so a refresh doesn't dump the
  // user's bookmarked replies. Lazy-init reads the stored JSON once
  // on mount; an effect below mirrors every change back. We pin the
  // namespace to consumer_rent so a future per-mode shelf doesn't
  // collide.
  const [saved, setSaved] = useState<SavedItem[]>(() => {
    if (typeof window === "undefined") return [];
    try {
      const raw = window.localStorage.getItem(STORAGE_SAVED.consumerRent);
      if (!raw) return [];
      const parsed: unknown = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      // Defensive validate — drop entries missing required fields so a
      // partially-corrupted localStorage doesn't crash the tray.
      return parsed.filter((x): x is SavedItem => {
        const o = x as Partial<SavedItem>;
        return typeof o?.id === "string" && typeof o?.title === "string" && typeof o?.preview === "string" && typeof o?.when === "string";
      });
    } catch {
      return [];
    }
  });
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(STORAGE_SAVED.consumerRent, JSON.stringify(saved));
    } catch {
      // Quota / private-browsing — silent. Saved still works in-memory.
    }
  }, [saved]);
  // Persistent history tray — backend-backed, scoped to consumer_rent
  // so the Buy/Agent surfaces' threads don't bleed into Rent's tray.
  // useChat dispatches STORAGE_EVENTS.conversationCreated when a new
  // conversation lands; useConversationList listens for it so the tray
  // refreshes mid-session without a page reload.
  const conv = useConversationList("consumer_rent");
  const history = useMemo<HistoryItem[]>(
    () => conv.conversations.map(convToHistoryItem),
    [conv.conversations],
  );

  const isWelcome = chat.history.length === 0 && chat.liveFlow.length === 0 && !chat.sending;

  const send = useCallback((prompt: string) => {
    const trimmed = prompt.trim();
    if (!trimmed) return;
    const brief = parseRentBrief(trimmed);
    if (brief) setLastBrief(brief);
    void chat.send(trimmed);
  }, [chat]);

  const newThread = useCallback(() => {
    // If a stream is mid-flight we must stop() first — clear() alone
    // wipes local state but leaves the BE producer alive, so its tokens
    // would race into the freshly-started next thread. stop() tells the
    // BE to abort, then clear() wipes useChat state + the localStorage
    // conversation_id so the next ask opens a brand-new thread. The
    // archived thread will surface in the history tray once useChat
    // emits STORAGE_EVENTS.conversationCreated for the next first message.
    if (chat.sending) chat.stop();
    chat.clear();
    setLastBrief(null);
  }, [chat]);

  const bookmark = useCallback((item: { id: string; title: string; preview: string }) => {
    setSaved((s) => s.some((x) => x.id === item.id) ? s : [{ ...item, when: nowLabel(isZh) }, ...s]);
  }, [isZh]);

  // Memoised set so AiMessage can flip the Save button into a "Saved"
  // state without scanning the array on every render. Without this the
  // button gave no visual feedback when clicked, so users assumed the
  // click hadn't registered (it had — the saved tray showed the entry).
  const savedIds = useMemo(() => new Set(saved.map((s) => s.id)), [saved]);

  const toggleLang = useCallback(() => {
    void i18n.changeLanguage(isZh ? "en" : "zh");
  }, [i18n, isZh]);

  return (
    <div data-track="rent" className="flex h-[100dvh] flex-col overflow-hidden bg-bg text-text">
      <RentTopbar
        panel={panel}
        onPanel={setPanel}
        isZh={isZh}
        onToggleLang={toggleLang}
        onSwitchToBuy={() => navigate("/buy")}
        onBackHome={() => navigate("/")}
        hasThread={!isWelcome}
        onNewThread={newThread}
      />

      <main className="relative flex-1 overflow-hidden">
        {isWelcome ? (
          <RentWelcome isZh={isZh} onAsk={send} onPanel={setPanel} />
        ) : (
          <RentLiveThread chat={chat} isZh={isZh} send={send} onBookmark={bookmark} savedIds={savedIds} lastBrief={lastBrief} />
        )}
      </main>

      {/* Right-side trays */}
      {panel === "history"   && <HistoryTray   history={history} isZh={isZh} onClose={() => setPanel(null)} onPick={(item) => { setPanel(null); setLastBrief(null); void chat.open(item.id); }} />}
      {panel === "saved"     && <SavedTray     saved={saved} isZh={isZh} onClose={() => setPanel(null)} onPick={(item) => { setPanel(null); send(item.title); }} onRemove={(id) => setSaved((s) => s.filter((x) => x.id !== id))} />}
      {panel === "tools"     && <ToolsTray     isZh={isZh} onClose={() => setPanel(null)} onAsk={(q) => { setPanel(null); send(q); }} />}
      {panel === "checklist" && <ChecklistTray isZh={isZh} onClose={() => setPanel(null)} onAsk={(q) => { setPanel(null); send(q); }} />}

      {/* Used by typing indicator + composer accent border + tray slide-in */}
      <style>{`
        @keyframes rent-pulse {
          0%, 80%, 100% { opacity: 0.25; transform: scale(0.85); }
          40% { opacity: 1; transform: scale(1); }
        }
        .rent-msg-in { animation: rent-fade-in 240ms cubic-bezier(0.16,1,0.3,1) both; }
        @keyframes rent-fade-in {
          from { opacity: 0; transform: translateY(4px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes rent-fade-overlay {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes rent-slide-in-r {
          from { transform: translateX(8%); opacity: 0; }
          to   { transform: translateX(0); opacity: 1; }
        }
      `}</style>
    </div>
  );
}

function nowLabel(isZh: boolean) {
  return isZh ? "刚刚" : "Just now";
}

// convToHistoryItem — map a server ConversationSummary into the local
// HistoryItem shape the tray renders. v1 surfaces title only; preview
// stays empty until we add a first-assistant-message snapshot column
// server-side. The displayed `when` is a locale-friendly absolute date
// derived from updated_at so the tray ordering matches what the server
// returns.
function convToHistoryItem(c: ConversationSummary): HistoryItem {
  const title = c.title.trim() || (c.id.slice(0, 8));
  let when = "";
  if (c.updated_at) {
    const d = new Date(c.updated_at);
    if (!Number.isNaN(d.getTime())) {
      when = d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
    }
  }
  return { id: c.id, title, preview: "", when };
}

/* ── Topbar ─────────────────────────────────────────────────────────── */

function RentTopbar({
  panel, onPanel, isZh, onToggleLang, onSwitchToBuy, onBackHome, hasThread, onNewThread,
}: {
  panel: Panel;
  onPanel: (p: Panel) => void;
  isZh: boolean;
  onToggleLang: () => void;
  onSwitchToBuy: () => void;
  onBackHome: () => void;
  hasThread: boolean;
  onNewThread: () => void;
}) {
  const navItems: { id: NonNullable<Panel>; icon: JSX.Element; label: string; desc: string }[] = [
    { id: "tools",     icon: <CalcIcon />,     label: isZh ? "工具"   : "Tools",     desc: isZh ? "快速计算器" : "Quick calculators" },
    { id: "checklist", icon: <CheckIcon />,    label: isZh ? "签约清单" : "Checklist", desc: isZh ? "租约前确认" : "Lease checklist" },
    { id: "saved",     icon: <PinIcon />,      label: isZh ? "收藏"   : "Saved",     desc: isZh ? "保存的回答" : "Bookmarked" },
    { id: "history",   icon: <HistoryIcon />,  label: isZh ? "历史"   : "History",   desc: isZh ? "过往提问"   : "Past questions" },
  ];

  // relative + z-[60] keeps the topbar clickable on top of the tray
  // overlay (TrayShell uses z-50). Without this, opening a tray traps
  // the user — backdrop swallows the next nav-button click instead of
  // switching trays.
  return (
    <header className="relative z-[60] flex shrink-0 items-center justify-between border-b border-border-subtle bg-surface px-4 py-2.5 sm:px-6">
      <div className="flex items-center gap-3">
        <button
          onClick={onBackHome}
          title={isZh ? "回到入口" : "Back to gateway"}
          className="flex items-center gap-2"
        >
          <LogoIcon size={24} />
          <span style={{ fontFamily: "var(--font-serif)" }} className="hidden text-[16px] font-semibold tracking-[-0.01em] sm:inline">
            ProProp
          </span>
        </button>
        {/* Badge hidden on mobile — at 375px the header was already
            fighting 4 nav icons + lang toggle + switch-to-buy + the
            mid-thread "+" button, and the badge was the cheapest thing
            to drop. Logo alone is enough mode signaling on mobile. */}
        <span
          className="hidden rounded-full px-2 py-[2px] text-[10.5px] font-semibold uppercase tracking-[0.06em] sm:inline-block"
          style={{ background: "var(--rent-accent-dim)", color: "var(--rent-accent)" }}
        >
          {isZh ? "租房中" : "Renting"}
        </span>
      </div>

      <div className="flex items-center gap-1">
        {navItems.map((it) => {
          const active = panel === it.id;
          return (
            <button
              key={it.id}
              onClick={() => onPanel(active ? null : it.id)}
              title={it.desc}
              className="flex items-center gap-1.5 rounded-md px-2.5 py-[6px] text-[12px] font-medium transition"
              style={{
                background: active ? "var(--rent-accent-dim)" : "transparent",
                color: active ? "var(--rent-accent)" : "var(--text-sec)",
              }}
              onMouseEnter={(e) => {
                if (!active) (e.currentTarget as HTMLButtonElement).style.background = "var(--color-surface-2)";
              }}
              onMouseLeave={(e) => {
                if (!active) (e.currentTarget as HTMLButtonElement).style.background = "transparent";
              }}
            >
              <span className="opacity-80">{it.icon}</span>
              <span className="hidden sm:inline">{it.label}</span>
            </button>
          );
        })}

        <span className="mx-1 hidden h-[18px] w-px bg-border-subtle sm:inline-block" />

        {hasThread && (
          <button
            onClick={onNewThread}
            className="ml-1 flex items-center gap-1.5 rounded-md px-2.5 py-[6px] text-[12px] font-semibold transition"
            style={{ background: "var(--rent-accent)", color: "var(--rent-accent-on)" }}
            title={isZh ? "新对话" : "New chat"}
          >
            <PlusSmIcon />
            <span className="hidden sm:inline">{isZh ? "新对话" : "New chat"}</span>
          </button>
        )}

        <button
          onClick={onToggleLang}
          title={isZh ? "切换到 English" : "Switch to 中文"}
          className="rounded-md border border-border px-2 py-[3px] text-[11px] font-medium text-text-muted transition hover:border-border-strong hover:text-text"
        >
          <span className={isZh ? "opacity-50" : "font-bold"}>EN</span>
          <span className="mx-1 text-text-dim">·</span>
          <span className={isZh ? "font-bold" : "opacity-50"}>中</span>
        </button>

        <button
          onClick={onSwitchToBuy}
          className="ml-1 flex items-center gap-1.5 rounded-md border border-border px-2.5 py-[6px] text-[12px] font-medium text-text-sec transition hover:border-border-strong hover:text-text"
          title={isZh ? "切换到买家工作台" : "Switch to buying"}
        >
          <SwapIcon />
          <span className="hidden sm:inline">{isZh ? "切到买房" : "Switch to buy"}</span>
        </button>
      </div>
    </header>
  );
}

/* ── Welcome screen ────────────────────────────────────────────────── */

const RENT_PROMPTS: { id: string; iconName: PromptIcon; titleEn: string; titleZh: string; qEn: string; qZh: string }[] = [
  // Listing-brief style — these hit the new listingsBriefAnswer router
  // and surface a project shortlist + percentile commentary.
  { id: "brief1", iconName: "chart",   titleEn: "$3–4k · Tanjong Pagar · 1BR",     titleZh: "$3-4k / 丹戎巴葛 / 1 房",  qEn: "$3-4k / Tanjong Pagar / 1BR / move-in May / 5min from MRT",                qZh: "$3-4k / 丹戎巴葛 / 1 房 / 5 月入住 / 离地铁 5 分钟内" },
  { id: "brief2", iconName: "key",     titleEn: "$5–7k · River Valley · 2BR",      titleZh: "$5-7k / 河谷 / 2 房",     qEn: "$5-7k / River Valley / 2BR / move-in flexible",                            qZh: "$5-7k / 河谷区 / 2 房 / 入住时间灵活" },
  // Knowledge / lease workflow questions — fall through to the Q&A bank.
  { id: "stamp",  iconName: "receipt", titleEn: "Stamp duty on my lease",          titleZh: "租约印花税",              qEn: "What's the stamp duty on a S$3,500/month 12-month lease?",                qZh: "S$3,500/月、12 个月的租约印花税多少？" },
  { id: "wp",     iconName: "shield",  titleEn: "Can I rent HDB on Work Permit?",  titleZh: "WP 持有者能租组屋吗",      qEn: "Can a Work Permit holder rent an HDB flat in Singapore?",                 qZh: "Work Permit 持有者能在新加坡租组屋吗？" },
  { id: "dip",    iconName: "book",    titleEn: "What's a 'diplomatic clause'?",   titleZh: "什么是外交条款",          qEn: "Explain the diplomatic clause in a Singapore tenancy agreement.",         qZh: "解释一下新加坡租约里的外交条款（diplomatic clause）。" },
  { id: "agent",  iconName: "people",  titleEn: "Agent fees in SG",                titleZh: "经纪人佣金谁付",          qEn: "Who pays the agent commission on a 1-year lease in Singapore?",            qZh: "新加坡 1 年租约的经纪人佣金谁付？" },
];

const SHORTCUTS: { id: NonNullable<Panel>; iconName: ShortcutIcon; titleEn: string; titleZh: string; descEn: string; descZh: string }[] = [
  { id: "tools",     iconName: "calc",  titleEn: "Quick tools",      titleZh: "快速工具",  descEn: "Stamp duty, deposit, fair-rent",     descZh: "印花税、押金、合理租金"   },
  { id: "checklist", iconName: "check", titleEn: "Lease checklist",  titleZh: "签约清单",  descEn: "What to check before signing",       descZh: "签约前要核实什么"        },
  { id: "saved",     iconName: "pin",   titleEn: "Saved answers",    titleZh: "已收藏",    descEn: "Your bookmarked replies",            descZh: "你保存的 AI 回答"        },
];

function RentWelcome({
  isZh, onAsk, onPanel,
}: {
  isZh: boolean;
  onAsk: (prompt: string) => void;
  onPanel: (p: Panel) => void;
}) {
  const { t } = useTranslation();
  const [composerValue, setComposerValue] = useState("");

  function submit() {
    const trimmed = composerValue.trim();
    if (!trimmed) return;
    setComposerValue("");
    onAsk(trimmed);
  }

  return (
    <div className="scrollbar-thin h-full overflow-y-auto">
      <div
        className="mx-auto flex min-h-full max-w-[720px] flex-col px-6 sm:px-7"
        style={{ paddingTop: "min(8vh, 72px)", paddingBottom: 56 }}
      >
        {/* Eyebrow chip */}
        <div className="mb-6">
          <span className="inline-flex items-center gap-2 rounded-full border border-border bg-surface px-3 py-[5px] text-[11.5px] text-text-sec">
            <span className="h-1.5 w-1.5 rounded-full" style={{ background: "var(--rent-accent)" }} />
            {isZh ? "新加坡租房 · 由 URA / HDB / IRAS 数据驱动" : "Renting in Singapore · powered by URA / HDB / IRAS data"}
          </span>
        </div>

        {/* Hero */}
        <h1
          className="m-0 font-medium tracking-[-0.022em]"
          style={{ fontFamily: "var(--font-serif)", fontSize: "clamp(34px, 4.6vw, 50px)", lineHeight: 1.08 }}
        >
          {isZh ? "想住在哪里？告诉我。" : "Where do you want to live? Just tell me."}
        </h1>
        <p className="mt-3 max-w-[580px] text-[15px] leading-[1.55] text-text-sec sm:text-[16px]">
          {isZh
            ? "告诉我你的租房要求 — 位置、通勤时间、价格、户型、入住时间 — 我直接帮你列出最优质、性价比最高的房源。"
            : "Tell me what you're looking for — location, commute time, budget, layout, move-in date — and I'll surface the best-value listings for you."}
          {" "}
          {/* Discovery clause: the door-to-door commute tool already exists
              but nothing told tenants they can ask it in chat. This one
              line makes it findable. */}
          <span className="text-text-muted">{t("rentCommute.welcomeClause")}</span>
        </p>

        {/* Big composer */}
        <div className="mt-7">
          <Composer
            value={composerValue}
            onChange={setComposerValue}
            onSubmit={submit}
            placeholder={isZh
              ? "试试：'$3-4k / Tanjong Pagar 附近 / 1 房 / 5 月入住 / 离地铁 5 分钟以内'"
              : "Try: '$3–4k · near Tanjong Pagar · 1BR · move-in May · ≤5 min from MRT'"}
            large
            accent="var(--rent-accent)"
            accentOn="var(--rent-accent-on)"
          />
        </div>

        {/* Pick one — 6 prompt cards */}
        <div className="mt-8">
          <SectionLabel>{isZh ? "或者点一个开始" : "Or pick one to start"}</SectionLabel>
          <div className="mt-2.5 grid grid-cols-1 gap-2 sm:grid-cols-2">
            {RENT_PROMPTS.map((p) => (
              <PromptCard
                key={p.id}
                icon={<PromptIconEl name={p.iconName} />}
                label={isZh ? p.titleZh : p.titleEn}
                onClick={() => onAsk(isZh ? p.qZh : p.qEn)}
              />
            ))}
            {/* Commute prompt card — the door-to-door transit_commute_estimate
                tool is already wired into the consumer_rent stream; this card
                is what actually surfaces it to a lazy tenant. Title +
                seed question come from i18n so both locales stay in parity. */}
            <PromptCard
              key="commute"
              icon={<MrtIcon />}
              label={t("rentCommute.promptCardTitle")}
              onClick={() => onAsk(t("rentCommute.promptQuestion"))}
            />
          </div>
        </div>

        {/* Skip ahead — 3 shortcut cards */}
        <div className="mt-9">
          <SectionLabel>{isZh ? "或者直接跳到工具" : "Skip ahead"}</SectionLabel>
          <div className="mt-2.5 grid grid-cols-1 gap-2 sm:grid-cols-3">
            {SHORTCUTS.map((s) => (
              <ShortcutCard
                key={s.id}
                icon={<ShortcutIconEl name={s.iconName} />}
                title={isZh ? s.titleZh : s.titleEn}
                desc={isZh ? s.descZh : s.descEn}
                onClick={() => onPanel(s.id)}
              />
            ))}
          </div>
        </div>

        {/* Footer 3-col explainer */}
        <div className="mt-auto pt-12">
          <hr className="border-0 border-t border-border-subtle" />
          <div className="mt-4 grid grid-cols-1 gap-5 text-[12.5px] text-text-muted sm:grid-cols-3">
            <div>
              <FooterLabel>{isZh ? "这是什么" : "What this is"}</FooterLabel>
              <p className="mt-1.5 leading-[1.55]">
                {isZh
                  ? "一个直接答你新加坡租房问题的 AI。所有数字都从 URA、HDB、IRAS 等官方数据集查出来再回答。"
                  : "A straight-talking AI for Singapore renting questions. Numbers come from URA, HDB, IRAS — never guessed."}
              </p>
            </div>
            <div>
              <FooterLabel>{isZh ? "不是什么" : "What it isn't"}</FooterLabel>
              <p className="mt-1.5 leading-[1.55]">
                {isZh
                  ? "不是房源平台、不替你看房、不是经纪人。具体房源细节 / 议价 / 看房安排请直接和持牌经纪人联系。"
                  : "Not a listings portal, not an agent, not a viewing concierge. For real listings / negotiation / viewings, work with a CEA-licensed pro."}
              </p>
            </div>
            <div>
              <FooterLabel>{isZh ? "想买房？" : "Buying instead?"}</FooterLabel>
              <p className="mt-1.5 leading-[1.55]">
                {isZh ? "买房是另一回事 — 长流程、要工作台。" : "Buying is another beast — long process, needs a workspace."}
                <br />
                <Link to="/buy" className="mt-1 inline-flex items-center gap-1 font-medium" style={{ color: "var(--rent-accent)" }}>
                  {isZh ? "切到买家工作台" : "Switch to buyer workspace"} →
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-[10.5px] font-semibold uppercase tracking-[0.1em] text-text-muted">
      {children}
    </div>
  );
}

function FooterLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-[10.5px] font-semibold uppercase tracking-[0.08em]" style={{ color: "var(--rent-accent)" }}>
      {children}
    </div>
  );
}

function PromptCard({ icon, label, onClick }: { icon: React.ReactNode; label: string; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="group flex items-center gap-2.5 rounded-[10px] border border-border bg-surface px-3 py-2.5 text-left transition"
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLButtonElement).style.borderColor = "var(--rent-accent)";
        (e.currentTarget as HTMLButtonElement).style.background = "var(--rent-accent-dim)";
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLButtonElement).style.borderColor = "var(--rule)";
        (e.currentTarget as HTMLButtonElement).style.background = "var(--color-surface)";
      }}
    >
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md" style={{ background: "var(--rent-accent-dim)", color: "var(--rent-accent)" }}>
        {icon}
      </span>
      <span className="flex-1 text-[13px] font-medium text-text">{label}</span>
      <ArrowIcon />
    </button>
  );
}

function ShortcutCard({ icon, title, desc, onClick }: { icon: React.ReactNode; title: string; desc: string; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="flex items-start gap-2.5 rounded-[10px] border border-border bg-surface px-3 py-3 text-left transition"
      onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.borderColor = "var(--rent-accent)"; }}
      onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.borderColor = "var(--rule)"; }}
    >
      <span className="mt-[1px] flex h-7 w-7 shrink-0 items-center justify-center rounded-md" style={{ background: "var(--color-surface-2)", color: "var(--rent-accent)" }}>
        {icon}
      </span>
      <div className="min-w-0">
        <div className="text-[13px] font-semibold text-text">{title}</div>
        <div className="mt-[2px] text-[11.5px] text-text-muted leading-[1.4]">{desc}</div>
      </div>
    </button>
  );
}

/* ── Composer (shared with thread later) ───────────────────────────── */

function Composer({
  value, onChange, onSubmit, placeholder, large, accent, accentOn,
}: {
  value: string;
  onChange: (v: string) => void;
  onSubmit: () => void;
  placeholder: string;
  large?: boolean;
  accent: string;
  accentOn: string;
}) {
  const taRef = useRef<HTMLTextAreaElement | null>(null);
  function onKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    // Don't submit while an IME composition is active — pinyin/kana
    // selection presses Enter to commit a candidate, and we'd otherwise
    // ship a half-typed Chinese/Japanese sentence to the backend. The
    // 229 keyCode check covers Safari, which doesn't always set
    // isComposing on the React synthetic event.
    if (e.nativeEvent.isComposing || e.keyCode === 229) return;
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      onSubmit();
    }
  }
  function autoResize(el: HTMLTextAreaElement) {
    el.style.height = "auto";
    el.style.height = Math.min(el.scrollHeight, large ? 220 : 140) + "px";
  }
  // Reset height whenever value changes externally (post-send clear,
  // tray onAsk overwrite, language toggle changing placeholder length).
  // Without this, the textarea kept the auto-grown height from the last
  // long input even after the value reset to "" — leaving a giant empty
  // composer floating at the bottom.
  useEffect(() => {
    if (taRef.current) autoResize(taRef.current);
    // autoResize closes over `large`; including it (and re-running on
    // value change) is the intent.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, large]);
  return (
    <div
      className="flex items-end gap-2 rounded-[14px] border bg-surface p-2.5 transition focus-within:shadow-md"
      style={{ borderColor: "var(--rule)" }}
      onFocus={(e) => { (e.currentTarget as HTMLDivElement).style.borderColor = accent; }}
      onBlur={(e) => { (e.currentTarget as HTMLDivElement).style.borderColor = "var(--rule)"; }}
    >
      <textarea
        ref={taRef}
        value={value}
        onChange={(e) => { onChange(e.target.value); autoResize(e.currentTarget); }}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        rows={1}
        className="w-full flex-1 resize-none bg-transparent px-2 py-1.5 text-text outline-none placeholder:text-text-muted"
        style={{ fontSize: large ? 15 : 14, lineHeight: 1.55 }}
      />
      <button
        type="button"
        onClick={onSubmit}
        disabled={!value.trim()}
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full transition disabled:cursor-not-allowed disabled:opacity-40"
        style={{ background: accent, color: accentOn }}
        title="Send"
      >
        <SendArrowIcon />
      </button>
    </div>
  );
}

/* ── Tiny icon set (kept inline so this page stays self-contained) ── */

type PromptIcon = "chart" | "receipt" | "shield" | "book" | "key" | "people";
function PromptIconEl({ name }: { name: PromptIcon }) {
  switch (name) {
    case "chart":
      return <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2 11l3-3 2.5 2.5L12 4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" /></svg>;
    case "receipt":
      return <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M3 1.5h8v11l-2-1-2 1-2-1-2 1z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" /><path d="M5 4.5h4M5 6.5h4M5 8.5h2.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" /></svg>;
    case "shield":
      return <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M7 1.5l4.5 1.5v3.5C11.5 9.5 9.5 11.5 7 12.5 4.5 11.5 2.5 9.5 2.5 6.5V3z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" /></svg>;
    case "book":
      return <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2 2.5h4.5a1.5 1.5 0 0 1 1.5 1.5v8a1 1 0 0 0-1-1H2zM12 2.5H7.5A1.5 1.5 0 0 0 6 4v8a1 1 0 0 1 1-1h5z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" /></svg>;
    case "key":
      return <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><circle cx="4" cy="10" r="2.2" stroke="currentColor" strokeWidth="1.3" /><path d="M5.5 8.5l4-4M8 6l1.5 1.5M9.5 4.5l1.5 1.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" /></svg>;
    case "people":
      return <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><circle cx="5" cy="5" r="2" stroke="currentColor" strokeWidth="1.3" /><circle cx="10" cy="6" r="1.5" stroke="currentColor" strokeWidth="1.3" /><path d="M1.5 12c0-1.7 1.6-3 3.5-3s3.5 1.3 3.5 3M9 12c0-1.2 1-2.2 2.5-2.2" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" /></svg>;
  }
}

type ShortcutIcon = "calc" | "check" | "pin";
function ShortcutIconEl({ name }: { name: ShortcutIcon }) {
  switch (name) {
    case "calc":  return <CalcIcon />;
    case "check": return <CheckIcon />;
    case "pin":   return <PinIcon />;
  }
}

function CalcIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <rect x="2" y="1.5" width="10" height="11" rx="1.5" stroke="currentColor" strokeWidth="1.3" />
      <rect x="3.8" y="3.2" width="6.4" height="2" rx="0.4" stroke="currentColor" strokeWidth="1.2" />
      <circle cx="4.7" cy="7.4" r="0.6" fill="currentColor" />
      <circle cx="7" cy="7.4" r="0.6" fill="currentColor" />
      <circle cx="9.3" cy="7.4" r="0.6" fill="currentColor" />
      <circle cx="4.7" cy="9.7" r="0.6" fill="currentColor" />
      <circle cx="7" cy="9.7" r="0.6" fill="currentColor" />
      <circle cx="9.3" cy="9.7" r="0.6" fill="currentColor" />
    </svg>
  );
}
function CheckIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <path d="M2.5 7l3 3 6-6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
function PinIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <path d="M7 1.5L9 3.5l-1 1 1.5 1.5L8 7.5l-2-2L4 7.5 2.5 6l4.5-4.5z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
      <path d="M5 8.5L2.5 12.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  );
}
function HistoryIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <path d="M2.5 7a4.5 4.5 0 1 0 1.4-3.3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
      <path d="M2 2v2.7h2.7M7 4.5V7l1.8 1.2" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
// MrtIcon — a small train glyph for the commute prompt card. Kept inline
// with the rest of this page's self-contained icon set.
function MrtIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <rect x="3.5" y="1.5" width="7" height="8.5" rx="2" stroke="currentColor" strokeWidth="1.3" />
      <path d="M3.5 6h7" stroke="currentColor" strokeWidth="1.2" />
      <circle cx="5.5" cy="8" r="0.6" fill="currentColor" />
      <circle cx="8.5" cy="8" r="0.6" fill="currentColor" />
      <path d="M4.5 10.5L3 12.5M9.5 10.5L11 12.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  );
}
function SwapIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
      <path d="M2.5 4.5h8M8.5 2.5L10.5 4.5 8.5 6.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M11.5 9.5h-8M5.5 11.5L3.5 9.5 5.5 7.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
function ArrowIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
      <path d="M3 7h8m0 0L7.5 3.5M11 7l-3.5 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ color: "var(--text-muted)" }} />
    </svg>
  );
}
function SendArrowIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <path d="M7 11V3M3.5 6.5L7 3l3.5 3.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
function PlusSmIcon() {
  return (
    <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
      <path d="M6 2v8M2 6h8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}
function CopyIcon() {
  return (
    <svg width="11" height="11" viewBox="0 0 14 14" fill="none">
      <rect x="2" y="2" width="8" height="8" rx="1.2" stroke="currentColor" strokeWidth="1.3" />
      <path d="M5 12.5h6.5a1 1 0 0 0 1-1V5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  );
}
function SaveIcon() {
  return (
    <svg width="11" height="11" viewBox="0 0 14 14" fill="none">
      <path d="M3 1.5h8v11l-4-2.2L3 12.5z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
    </svg>
  );
}
/* ──────────────────────────────────────────────────────────────────
   RentLiveThread — renders the live useChat history. Text replies
   stream in as markdown; if the most recent user turn was a brief,
   we append a deterministic <ListingShortlist /> below the latest AI
   reply since the LLM has no live listings feed of its own.
   ────────────────────────────────────────────────────────────────── */

function RentLiveThread({
  chat, isZh, send, onBookmark, savedIds, lastBrief,
}: {
  chat: ReturnType<typeof useChat>;
  isZh: boolean;
  send: (q: string) => void;
  onBookmark: (item: { id: string; title: string; preview: string }) => void;
  savedIds: Set<string>;
  lastBrief: RentBrief | null;
}) {
  const scroller = useRef<HTMLDivElement | null>(null);
  const [composerValue, setComposerValue] = useState("");

  // Visible turns: drop tool_trace rows but KEEP gen_ui — the AI's
  // present_data visualisations (charts / comparison tables) must render,
  // not be silently dropped (PROP-201). Consecutive assistant chunks
  // (LLM mid-stream narration before a tool call + the post-tool final
  // answer) are merged into one bubble so they don't render as two
  // separate "messages" each carrying their own Save/Copy strip.
  const visible = useMemo(() => {
    const out: ChatMessage[] = [];
    for (const m of chat.history) {
      if (m.role !== "user" && m.role !== "assistant" && m.role !== "error" && m.role !== "gen_ui") continue;
      const prev = out[out.length - 1];
      if (m.role === "assistant" && prev && prev.role === "assistant") {
        const merged = `${(prev as { content: string }).content}\n\n${m.content}`.trim();
        out[out.length - 1] = { ...(prev as Extract<ChatMessage, { role: "assistant" }>), content: merged };
      } else {
        out.push(m);
      }
    }
    return out;
  }, [chat.history]);

  useEffect(() => {
    if (scroller.current) scroller.current.scrollTop = scroller.current.scrollHeight;
  }, [visible.length, chat.sending]);

  function submit() {
    const trimmed = composerValue.trim();
    if (!trimmed) return;
    setComposerValue("");
    send(trimmed);
  }

  // Did the LATEST completed assistant turn lean on the commute tool?
  // We scan chat.history (the persisted/committed transcript, not the
  // in-flight liveFlow) for a transit_commute_estimate tool_trace that
  // lands after the last user turn. When it does, the tenant is at the
  // highest-intent moment ("I care about this route") — so we swap the
  // generic ≥2-reply lead card for a commute-flavoured one tagged
  // LEAD_SOURCE.rentCommute, and show it immediately instead of waiting
  // for the reply count to hit 2.
  const usedCommuteThisTurn = useMemo(
    () => lastAssistantUsedCommute(chat.history),
    [chat.history],
  );

  return (
    <div className="flex h-full flex-col">
      <div
        ref={scroller}
        className="scrollbar-thin flex-1 overflow-y-auto"
        // aria-live=polite makes screen readers announce streaming AI
        // tokens as they arrive without interrupting the user. atomic=
        // false so each chunk is read incrementally, not the full thread
        // re-read on every token.
        aria-live="polite"
        aria-atomic="false"
        aria-busy={chat.sending}
      >
        <div className="mx-auto max-w-[720px] px-6 py-7 sm:px-7">
          {visible.map((m, i) => {
            if (m.role === "user") {
              return <YouMessage key={i}>{m.content}</YouMessage>;
            }
            if (m.role === "gen_ui") {
              // PROP-201: render the AI's structured visualisation instead
              // of dropping it (same component ChatUI uses).
              return <GenUIBlock key={i} payload={(m as Extract<ChatMessage, { role: "gen_ui" }>).payload} />;
            }
            if (m.role === "error") {
              return (
                <AiMessage key={i} isZh={isZh}>
                  <div className="text-[13.5px]" style={{ color: "var(--neg)" }}>
                    {isZh ? "抱歉，刚刚出了点问题：" : "Something went wrong:"} {m.content}
                  </div>
                </AiMessage>
              );
            }
            // assistant message — markdown text, optional brief shortlist below.
            // The visible filter above narrows the runtime shape but not the
            // type, so we cast to the concrete assistant variant for clean
            // property access.
            const am = m as Extract<ChatMessage, { role: "assistant" }>;
            const userBefore = visible.slice(0, i).reverse().find((x) => x.role === "user");
            const userBeforeText = userBefore && userBefore.role === "user"
              ? (userBefore as Extract<ChatMessage, { role: "user" }>).content
              : "";
            return (
              <AiMessage
                key={i}
                isZh={isZh}
                markdown={am.content}
                onCopy={() => copyText(am.content)}
                saved={savedIds.has(stableBookmarkId(i, am.content))}
                onBookmark={() => onBookmark({
                  // Stable id derived from message position + content
                  // signature so re-clicking Save on the same reply
                  // hits the de-dup branch in `bookmark()` instead of
                  // appending a fresh row each time. (Date.now() made
                  // every click look like a brand-new bookmark.)
                  id: stableBookmarkId(i, am.content),
                  title: userBeforeText || (isZh ? "已保存的回答" : "Saved answer"),
                  preview: stripMd(am.content).slice(0, 200),
                })}
              >
                {/* The hand-curated PROJECT_CATALOG / ListingShortlist
                    add-on was removed: it was occasionally surfacing
                    projects at S$3,700–4,200 below a S$2,500-budget brief
                    with an "in budget" tag, because the catalog was
                    placeholder data unaware of the user's price cap.
                    The LLM now calls rental_market_match for real URA
                    contracts on every brief, so the deterministic
                    add-on is both redundant and a correctness risk. */}
              </AiMessage>
            );
          })}
          {/* Live progress — when we have tool_call/tool_result events
              streaming in, render them as a compact step list so the
              user sees "searching Suntec → fetching MRT → matching
              leases" instead of static typing dots for 30-90s. */}
          {chat.sending && (visible.length === 0 || visible[visible.length - 1].role !== "assistant") && (
            <AiMessage isZh={isZh}>
              <ProgressTrace flow={chat.liveFlow} isZh={isZh} />
            </AiMessage>
          )}
          {/* Lead-capture card. Two triggers, one shared localStorage
              dedup guard (RENT_LEAD_KEY) so a tenant only ever sees ONE:
                - Commute trigger (higher intent): fires the moment the
                  latest assistant turn used transit_commute_estimate,
                  with commute-flavoured copy + source=rent_commute so
                  the pro inbox can attribute it.
                - Generic trigger: the original ≥2-AI-replies fallback,
                  source=rent-chat.
              We prefill district + budget from the most recent parsed
              brief so the agent already knows what the tenant wants. */}
          {!chat.sending && usedCommuteThisTurn ? (
            <RentLeadCard isZh={isZh} brief={lastBrief} source={LEAD_SOURCE.rentCommute} variant="commute" />
          ) : !chat.sending && chat.history.filter((m) => m.role === "assistant").length >= 2 ? (
            <RentLeadCard isZh={isZh} brief={lastBrief} source={LEAD_SOURCE.rentChat} variant="generic" />
          ) : null}
        </div>
      </div>

      {/* Composer fixed at the bottom of the thread */}
      <div className="border-t border-border-subtle bg-bg">
        <div className="mx-auto max-w-[720px] px-6 py-4 sm:px-7">
          <Composer
            value={composerValue}
            onChange={setComposerValue}
            onSubmit={submit}
            placeholder={isZh ? "继续追问…" : "Ask a follow-up…"}
            accent="var(--rent-accent)"
            accentOn="var(--rent-accent-on)"
          />
        </div>
      </div>
    </div>
  );
}

function copyText(text: string) {
  if (typeof navigator !== "undefined" && navigator.clipboard) {
    void navigator.clipboard.writeText(text);
  }
}

// lastAssistantUsedCommute — true when the MOST RECENT assistant turn
// leaned on the transit_commute_estimate tool. We walk chat.history back
// from the end: tool_trace rows for one assistant turn are persisted
// BEFORE that turn's assistant text (see useChat commit order), so we
// look for a transit_commute_estimate trace sitting after the last user
// message. Hitting the previous user turn first means the newest turn
// wasn't a commute turn (the trace belongs to an older exchange), so we
// stop and return false. This keeps the commute referral hook bound to
// the fresh commute answer, not a stale one from three turns ago.
export const COMMUTE_TOOL_NAME = "transit_commute_estimate";
export function lastAssistantUsedCommute(history: ChatMessage[]): boolean {
  for (let i = history.length - 1; i >= 0; i--) {
    const m = history[i];
    if (m.role === "user") return false;
    if (m.role === "tool_trace" && m.name === COMMUTE_TOOL_NAME) return true;
  }
  return false;
}

// stableBookmarkId derives a bookmark's identity PURELY from its
// position + content signature — never the server message id. The
// background re-hydrate in useChat patches a server id onto the
// assistant message after the fact; keying off it flipped the bookmark
// id from `idx-…` to `srv-…` mid-session, which lost the "Saved" state
// (savedIds is keyed by id) and let a re-click create a duplicate (the
// de-dup keys by id too). A content signature is invariant across that
// patch, keeping the optimistic and hydrated identities symmetric. (PROP-182)
export function stableBookmarkId(index: number, content: string): string {
  return `idx-${index}-${content.length}-${content.slice(0, 60)}`;
}

function stripMd(s: string): string {
  // Crude markdown-to-text for bookmark previews; strips backticks,
  // links, bold/italic markers, and headings without dragging in a
  // full markdown lib for one tiny use.
  return s
    .replace(/```[\s\S]*?```/g, "")
    .replace(/`([^`]*)`/g, "$1")
    .replace(/!\[[^\]]*]\([^)]*\)/g, "")
    .replace(/\[([^\]]*)]\([^)]*\)/g, "$1")
    .replace(/[*_~]+/g, "")
    .replace(/^#+\s*/gm, "")
    .replace(/\n{2,}/g, " ")
    .trim();
}

function YouMessage({ children }: { children: ReactNode }) {
  return (
    <div className="rent-msg-in mb-5 flex justify-end">
      {/* User bubble — accent-tinted so it reads as "your turn" against
          the neutral surface AI card. The solid border keeps it legible
          in both light + dark modes (alpha-only fills disappear into
          the dark bg). */}
      <div
        className="max-w-[78%] rounded-[14px] border px-3.5 py-2.5 text-[14px] leading-[1.55]"
        style={{
          background: "var(--accent-dim)",
          borderColor: "var(--accent)",
          color: "var(--text)",
        }}
      >
        {children}
      </div>
    </div>
  );
}

function AiMessage({
  children, isZh, markdown, onCopy, onBookmark, saved,
}: {
  children?: ReactNode;
  isZh: boolean;
  markdown?: string;
  onCopy?: () => void;
  onBookmark?: () => void;
  saved?: boolean;
}) {
  return (
    <div className="rent-msg-in mb-6 flex items-start gap-2.5">
      <div
        className="mt-[2px] flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-[10.5px] font-bold"
        style={{ background: "var(--rent-accent)", color: "var(--rent-accent-on)" }}
      >
        AI
      </div>
      <div className="min-w-0 flex-1">
        <div
          className="rounded-[14px] border bg-surface px-4 py-3 text-[14px] leading-[1.6] text-text"
          style={{ borderColor: "var(--rule)" }}
        >
          {markdown != null && (
            <div
              className="markdown"
              dangerouslySetInnerHTML={{ __html: renderMarkdown(markdown) }}
            />
          )}
          {children}
        </div>
        {(onCopy || onBookmark) && (
          <div className="mt-1.5 flex gap-1 pl-1">
            {onBookmark && (
              <button
                onClick={onBookmark}
                disabled={saved}
                className="flex items-center gap-1 rounded-md px-2 py-[3px] text-[11px] transition disabled:cursor-default"
                style={{
                  background: saved ? "var(--rent-accent-dim)" : "transparent",
                  color: saved ? "var(--rent-accent)" : "var(--color-text-muted)",
                }}
                onMouseEnter={(e) => {
                  if (!saved) (e.currentTarget as HTMLButtonElement).style.background = "var(--color-surface-2)";
                }}
                onMouseLeave={(e) => {
                  if (!saved) (e.currentTarget as HTMLButtonElement).style.background = "transparent";
                }}
              >
                {saved
                  ? <><CheckIcon /> {isZh ? "已收藏" : "Saved"}</>
                  : <><SaveIcon /> {isZh ? "收藏" : "Save"}</>
                }
              </button>
            )}
            {onCopy && (
              <button
                onClick={onCopy}
                className="flex items-center gap-1 rounded-md px-2 py-[3px] text-[11px] text-text-muted transition hover:text-text"
                onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "var(--color-surface-2)"; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "transparent"; }}
              >
                <CopyIcon /> {isZh ? "复制" : "Copy"}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// Friendly labels per tool name. Keep both languages here rather than
// in i18n bundles — the list is small + tool names rarely change, so
// inline avoids a translation round-trip when a new tool ships.
const TOOL_LABELS: Record<string, { zh: string; en: string }> = {
  search_location:           { zh: "搜索位置",        en: "Locating" },
  nearest_mrt:               { zh: "查附近 MRT",      en: "Nearest MRT" },
  transit_commute_estimate:  { zh: "估算通勤时间",    en: "Commute estimate" },
  rental_market_match:       { zh: "匹配真实租约",    en: "Matching leases" },
  rental_yield_estimate:     { zh: "估算租金回报",    en: "Rental yield" },
  check_hdb_eligibility:     { zh: "核查 HDB 资格",   en: "HDB eligibility" },
  cooling_measures_snapshot: { zh: "现行冷措施",      en: "Cooling measures" },
  present_data:              { zh: "整理数据",        en: "Formatting data" },
};

function toolLabel(name: string, isZh: boolean): string {
  const entry = TOOL_LABELS[name];
  if (entry) return isZh ? entry.zh : entry.en;
  // Fallback — prettify the snake_case tool name
  return name.replace(/_/g, " ");
}

type LiveFlowItem =
  | { kind: "thinking" }
  | { kind: "active"; name: string }
  | { kind: "tool"; name: string; projects?: string[] }
  | { kind: "text"; content: string }
  | { kind: "gen_ui"; payload: unknown };

function ProgressTrace({ flow, isZh }: { flow: LiveFlowItem[]; isZh: boolean }) {
  // Filter to just the tool-step events; thinking + text are handled
  // elsewhere (text streams into bubbles directly).
  const steps = flow.filter((f) => f.kind === "active" || f.kind === "tool");
  if (steps.length === 0) return <TypingDots />;
  return (
    <div className="flex flex-col gap-1.5 py-1">
      {steps.map((s, i) => {
        const done = s.kind === "tool";
        const name = (s as { name: string }).name;
        return (
          <div key={i} className="flex items-center gap-2 text-[12.5px]">
            <span
              className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[10px]"
              style={{
                background: done ? "var(--rent-accent)" : "transparent",
                border: done ? "none" : "1.5px solid var(--rent-accent)",
                color: "var(--rent-accent-on)",
              }}
            >
              {done ? "✓" : (
                <span
                  className="block h-1.5 w-1.5 rounded-full"
                  style={{ background: "var(--rent-accent)", animation: "rent-pulse 1s infinite" }}
                />
              )}
            </span>
            <span style={{ color: done ? "var(--text)" : "var(--text-sec)" }}>
              {toolLabel(name, isZh)}{done ? "" : (isZh ? "…" : "…")}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function TypingDots() {
  return (
    <div className="flex items-center gap-1.5 py-1">
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className="h-1.5 w-1.5 rounded-full"
          style={{
            background: "var(--rent-accent)",
            animation: `rent-pulse 1.2s ${i * 0.15}s infinite`,
          }}
        />
      ))}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────
   Listing-brief parser + URA-historical mock catalog. Used purely as
   a deterministic UI add-on attached to the latest AI message when
   the user's most recent turn parses as a brief — the LLM has no
   live listings feed of its own, so this fills the gap honestly.
   ────────────────────────────────────────────────────────────────── */

type RentBrief = {
  priceMin?: number;
  priceMax?: number;
  district?: string;     // canonical "D02" / "D09" / etc
  areaLabel?: string;    // user-facing label, e.g. "Tanjong Pagar" / "河谷"
  bedrooms?: number;     // 0 = studio, 1, 2, 3, 4
  moveIn?: string;       // free-text month label as the user wrote it
  commuteMin?: number;   // "5 min from MRT" → 5
};

// parseRentBrief: lightweight regex-based brief reader. Accepts both
// English and Chinese phrasing. Returns null unless ≥ 2 distinct fields
// could be extracted — fewer than that and the input is more likely a
// knowledge question, so we fall through to the regular Q&A router.
function parseRentBrief(q: string): RentBrief | null {
  const b: RentBrief = {};
  const ql = q.toLowerCase();

  // Price — accept "$3-4k", "3k-4k", "S$3000-4000", "$3.5k", "3k", "3000".
  // Assume "k" suffix means thousands; bare 3-digit numbers under 50
  // are unlikely to be rent so we skip them.
  const rangeM = q.match(/\$?\s*s?\$?\s*(\d+(?:\.\d+)?)\s*k?\s*[-–~~到至]\s*\$?\s*s?\$?\s*(\d+(?:\.\d+)?)\s*k\b/i);
  if (rangeM) {
    b.priceMin = Math.round(parseFloat(rangeM[1]) * 1000);
    b.priceMax = Math.round(parseFloat(rangeM[2]) * 1000);
  } else {
    const singleM = q.match(/\$?\s*s?\$?\s*(\d+(?:\.\d+)?)\s*k\b/i);
    if (singleM) {
      const v = Math.round(parseFloat(singleM[1]) * 1000);
      b.priceMin = Math.max(0, v - 500);
      b.priceMax = v + 500;
    } else {
      // Plain number range: "3000-4500"
      const rawRange = q.match(/(\d{3,5})\s*[-–~]\s*(\d{3,5})/);
      if (rawRange) {
        b.priceMin = parseInt(rawRange[1], 10);
        b.priceMax = parseInt(rawRange[2], 10);
      }
    }
  }

  // Areas — fuzzy lookup against a small alias table. First match wins.
  const AREA_ALIASES: { keys: string[]; district: string; label: string; labelZh: string }[] = [
    { keys: ["marina bay", "marina one", "marina"],     district: "D01", label: "Marina Bay",      labelZh: "滨海湾" },
    { keys: ["raffles", "raffles place"],               district: "D01", label: "Raffles Place",   labelZh: "莱佛士坊" },
    { keys: ["tanjong pagar", "tg pagar", "anson"],     district: "D02", label: "Tanjong Pagar",   labelZh: "丹戎巴葛" },
    { keys: ["chinatown", "outram"],                    district: "D02", label: "Chinatown",       labelZh: "牛车水" },
    { keys: ["tiong bahru", "tiong"],                   district: "D03", label: "Tiong Bahru",     labelZh: "中峇鲁" },
    { keys: ["queenstown", "redhill"],                  district: "D03", label: "Queenstown",      labelZh: "女皇镇" },
    { keys: ["river valley", "rivervalley", "robertson"], district: "D09", label: "River Valley",  labelZh: "河谷" },
    { keys: ["orchard", "somerset"],                    district: "D09", label: "Orchard",         labelZh: "乌节" },
    { keys: ["bukit timah", "newton hill"],             district: "D10", label: "Bukit Timah",     labelZh: "武吉知马" },
    { keys: ["holland", "holland village"],             district: "D10", label: "Holland Village", labelZh: "荷兰村" },
    { keys: ["novena", "newton"],                       district: "D11", label: "Novena",          labelZh: "诺维娜" },
    { keys: ["balestier", "thomson"],                   district: "D11", label: "Balestier",       labelZh: "马里士他" },
    { keys: ["bishan", "ang mo kio", "amk"],            district: "D20", label: "Bishan",          labelZh: "碧山" },
    { keys: ["punggol"],                                district: "D19", label: "Punggol",         labelZh: "榜鹅" },
    { keys: ["sengkang"],                               district: "D19", label: "Sengkang",        labelZh: "盛港" },
    // Chinese aliases
    { keys: ["丹戎巴葛", "巴葛"],                         district: "D02", label: "Tanjong Pagar",   labelZh: "丹戎巴葛" },
    { keys: ["滨海湾", "marina"],                        district: "D01", label: "Marina Bay",      labelZh: "滨海湾" },
    { keys: ["河谷", "罗伯逊"],                          district: "D09", label: "River Valley",    labelZh: "河谷" },
    { keys: ["乌节"],                                   district: "D09", label: "Orchard",         labelZh: "乌节" },
    { keys: ["碧山"],                                   district: "D20", label: "Bishan",          labelZh: "碧山" },
    { keys: ["中峇鲁"],                                 district: "D03", label: "Tiong Bahru",     labelZh: "中峇鲁" },
    { keys: ["武吉知马"],                                district: "D10", label: "Bukit Timah",     labelZh: "武吉知马" },
    { keys: ["诺维娜"],                                 district: "D11", label: "Novena",          labelZh: "诺维娜" },
  ];
  for (const alias of AREA_ALIASES) {
    if (alias.keys.some((k) => ql.includes(k) || q.includes(k))) {
      b.district = alias.district;
      b.areaLabel = alias.label;
      break;
    }
  }
  // Direct district code "D9" / "D 02" / "D02"
  if (!b.district) {
    const dm = q.match(/\bD\s*(\d{1,2})\b/i);
    if (dm) b.district = "D" + dm[1].padStart(2, "0");
  }

  // Bedrooms
  if (/\bstudio\b|开间|工作室/i.test(q)) b.bedrooms = 0;
  else if (/\b1\s*[brBR]|\b1[\s-]*bed|1\s*房|一房/i.test(q)) b.bedrooms = 1;
  else if (/\b2\s*[brBR]|\b2[\s-]*bed|2\s*房|两房|二房/i.test(q)) b.bedrooms = 2;
  else if (/\b3\s*[brBR]|\b3[\s-]*bed|3\s*房|三房/i.test(q)) b.bedrooms = 3;
  else if (/\b4\s*[brBR]|\b4[\s-]*bed|4\s*房|四房/i.test(q)) b.bedrooms = 4;

  // Move-in (loose — accept Chinese "5月"/"五月" or English month names)
  const monthsZh = ["1月","2月","3月","4月","5月","6月","7月","8月","9月","10月","11月","12月","一月","二月","三月","四月","五月","六月","七月","八月","九月","十月","十一月","十二月"];
  for (const m of monthsZh) {
    if (q.includes(m)) { b.moveIn = m; break; }
  }
  if (!b.moveIn) {
    const monthsEn = ["jan","feb","mar","apr","may","jun","jul","aug","sep","oct","nov","dec","january","february","march","april","may","june","july","august","september","october","november","december"];
    for (const m of monthsEn) {
      const re = new RegExp(`\\b${m}\\b`, "i");
      if (re.test(q)) { b.moveIn = m.charAt(0).toUpperCase() + m.slice(1); break; }
    }
  }

  // Commute hint
  const cm = q.match(/(\d+)\s*(?:min|分钟)/i);
  if (cm) b.commuteMin = parseInt(cm[1], 10);

  const fields = ["priceMin", "district", "bedrooms", "moveIn", "commuteMin"].filter((k) => b[k as keyof RentBrief] !== undefined).length;
  return fields >= 2 ? b : null;
}

/* ──────────────────────────────────────────────────────────────────
   Right-side trays — overlay + slide-in aside, all four share TrayShell.
   ────────────────────────────────────────────────────────────────── */

function TrayShell({
  title, subtitle, onClose, wide, children,
}: {
  title: string;
  subtitle?: string;
  onClose: () => void;
  wide?: boolean;
  children: ReactNode;
}) {
  // Esc to close — common a11y expectation for slide-in panels.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    // The tray sits *under* the rent topbar (which is z-[60]) so that
    // header nav buttons stay clickable while a tray is open — letting
    // the user switch trays in one click. Both the backdrop and the
    // aside start below the header (top = 53px, the header's measured
    // height), so the tray title isn't clipped.
    <div className="fixed inset-x-0 bottom-0 z-40" style={{ top: 53 }}>
      <div
        onClick={onClose}
        className="absolute inset-0 bg-black/45"
        style={{ animation: "rent-fade-overlay 200ms ease both" }}
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="absolute right-0 top-0 bottom-0 flex flex-col border-l bg-surface shadow-2xl"
        style={{
          width: wide ? "min(540px, 96vw)" : "min(420px, 92vw)",
          borderColor: "var(--rule)",
          animation: "rent-slide-in-r 240ms cubic-bezier(0.16,1,0.3,1) both",
        }}
      >
        <header className="flex items-center justify-between border-b border-border-subtle px-5 py-4">
          <div className="min-w-0">
            <h3 className="m-0 font-medium" style={{ fontFamily: "var(--font-serif)", fontSize: 17 }}>{title}</h3>
            {subtitle && <div className="mt-0.5 text-[11.5px] text-text-muted">{subtitle}</div>}
          </div>
          <button
            onClick={onClose}
            className="rounded-md p-1.5 text-text-muted transition hover:bg-surface-2 hover:text-text"
            title="Close"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M3 3l8 8M11 3l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
        </header>
        <div className="scrollbar-thin flex-1 overflow-y-auto py-2">{children}</div>
      </aside>
    </div>
  );
}

function EmptyTray({ icon, line, sub }: { icon: ReactNode; line: string; sub: string }) {
  return (
    <div className="px-9 py-10 text-center">
      <span
        className="mb-3 inline-flex h-11 w-11 items-center justify-center rounded-[12px]"
        style={{ background: "var(--color-surface-2)", color: "var(--text-muted)" }}
      >
        {icon}
      </span>
      <div className="text-[13.5px] font-medium text-text">{line}</div>
      <div className="mt-1 text-[12px] leading-[1.5] text-text-muted">{sub}</div>
    </div>
  );
}

/* History tray ────────────────────────────────────────── */

function HistoryTray({
  history, isZh, onClose, onPick,
}: {
  history: HistoryItem[];
  isZh: boolean;
  onClose: () => void;
  onPick: (item: HistoryItem) => void;
}) {
  return (
    <TrayShell
      title={isZh ? "过往提问" : "Past questions"}
      subtitle={isZh ? `${history.length} 条` : `${history.length} threads`}
      onClose={onClose}
    >
      {history.length === 0 ? (
        <EmptyTray
          icon={<HistoryIcon />}
          line={isZh ? "还没有历史" : "Nothing here yet"}
          sub={isZh ? "在主区问一句话，对话就会归档到这里。" : "Ask anything on the left and it will archive here."}
        />
      ) : (
        history.map((h) => (
          <button
            key={h.id}
            onClick={() => onPick(h)}
            className="block w-full border-b border-border-subtle px-5 py-3 text-left transition hover:bg-surface-2"
          >
            <div className="flex items-baseline justify-between gap-3">
              <span className="truncate text-[13.5px] font-semibold text-text">{h.title}</span>
              <span className="shrink-0 text-[11px] text-text-muted">{h.when}</span>
            </div>
            {h.preview && (
              <div
                className="mt-1 text-[12px] leading-[1.4] text-text-muted"
                style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}
              >
                {h.preview}
              </div>
            )}
          </button>
        ))
      )}
    </TrayShell>
  );
}

/* Saved tray ──────────────────────────────────────────── */

function SavedTray({
  saved, isZh, onClose, onPick, onRemove,
}: {
  saved: SavedItem[];
  isZh: boolean;
  onClose: () => void;
  onPick: (item: SavedItem) => void;
  onRemove: (id: string) => void;
}) {
  return (
    <TrayShell
      title={isZh ? "已收藏的回答" : "Saved answers"}
      subtitle={isZh ? `${saved.length} 条 · 保留在这台设备` : `${saved.length} bookmarked · saved on this device`}
      onClose={onClose}
    >
      {saved.length === 0 ? (
        <EmptyTray
          icon={<PinIcon />}
          line={isZh ? "暂无收藏" : "No saved answers yet"}
          sub={isZh ? "点 AI 回答下方的「收藏」就会出现在这里。" : "Tap Save under any AI reply to bookmark it here."}
        />
      ) : (
        <div className="px-3 py-2">
          {saved.map((s) => (
            <div
              key={s.id}
              className="mb-2 rounded-[12px] border bg-surface px-3.5 py-3"
              style={{ borderColor: "var(--rule)" }}
            >
              <div className="flex items-baseline justify-between gap-3">
                <h4 className="m-0 text-[13.5px] font-semibold text-text">{s.title}</h4>
                <span className="text-[11px] text-text-muted">{s.when}</span>
              </div>
              {s.preview && (
                <p className="mt-1.5 mb-2.5 text-[12.5px] leading-[1.5] text-text-sec">{s.preview}</p>
              )}
              <div className="flex flex-wrap gap-1.5">
                <button
                  onClick={() => onPick(s)}
                  className="flex items-center gap-1 rounded-md px-2.5 py-[5px] text-[12px] font-medium transition"
                  style={{ background: "var(--rent-accent-dim)", color: "var(--rent-accent)" }}
                >
                  {isZh ? "再问一次" : "Re-ask"}
                </button>
                <button
                  onClick={() => onRemove(s.id)}
                  className="flex items-center gap-1 rounded-md px-2.5 py-[5px] text-[12px] text-text-muted transition hover:bg-surface-2 hover:text-text"
                >
                  {isZh ? "移除" : "Remove"}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </TrayShell>
  );
}

/* Tools tray ──────────────────────────────────────────── */

function ToolsTray({ isZh, onClose, onAsk }: { isZh: boolean; onClose: () => void; onAsk: (q: string) => void }) {
  return (
    <TrayShell
      title={isZh ? "快速工具" : "Quick tools"}
      subtitle={isZh ? "几秒出答 · 复杂的问 AI" : "Self-serve answers in seconds. Anything weirder, ask AI."}
      onClose={onClose}
      wide
    >
      <ToolStampDuty isZh={isZh} onAsk={onAsk} />
      <ToolDeposit isZh={isZh} onAsk={onAsk} />
      <ToolFairness isZh={isZh} onAsk={onAsk} />
      <div className="p-4 text-center text-[11px] leading-[1.5] text-text-muted">
        {isZh
          ? <>印花税 · IRAS 2026。押金惯例 · CEA 市场实践。<br />仅供估算，不是报价。</>
          : <>Stamp duty · IRAS 2026. Deposit norms · CEA market practice.<br />These are estimates — never a quote.</>}
      </div>
    </TrayShell>
  );
}

function ToolCard({ icon, title, sub, children }: { icon: ReactNode; title: string; sub: string; children: ReactNode }) {
  return (
    <section
      className="m-3 rounded-[12px] border bg-surface px-4 py-3.5"
      style={{ borderColor: "var(--rule)" }}
    >
      <header className="mb-3 flex items-center gap-2.5">
        <span
          className="flex h-8 w-8 items-center justify-center rounded-md"
          style={{ background: "var(--rent-accent-dim)", color: "var(--rent-accent)" }}
        >
          {icon}
        </span>
        <div>
          <h4 className="m-0 text-[14px] font-semibold text-text">{title}</h4>
          <div className="text-[11.5px] text-text-muted">{sub}</div>
        </div>
      </header>
      {children}
    </section>
  );
}

function LabelBlock({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[10.5px] font-semibold uppercase tracking-[0.06em] text-text-muted">{label}</span>
      {children}
    </label>
  );
}

const inputCss: React.CSSProperties = {
  width: "100%",
  padding: "8px 10px",
  borderRadius: 8,
  border: "1px solid var(--rule)",
  background: "var(--color-surface-2)",
  color: "var(--text)",
  fontSize: 13,
  fontFamily: "var(--font-sans)",
};

function ToolStampDuty({ isZh, onAsk }: { isZh: boolean; onAsk: (q: string) => void }) {
  const [rent, setRent] = useState(3500);
  const [months, setMonths] = useState(12);
  const total = rent * months;
  const sd = Math.max(1, Math.round(total * 0.004));
  return (
    <ToolCard
      icon={<svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M3 1.5h8v11l-2-1-2 1-2-1-2 1z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" /><path d="M5 4.5h4M5 6.5h4M5 8.5h2.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" /></svg>}
      title={isZh ? "印花税估算" : "Stamp duty estimator"}
      sub={isZh ? "IRAS · 0.4% × 总租金（≤ 4 年）" : "IRAS · 0.4% × total rent (lease ≤ 4 yrs)"}
    >
      <div className="grid grid-cols-2 gap-2.5">
        <LabelBlock label={isZh ? "月租 (S$)" : "Monthly rent (S$)"}>
          <input style={inputCss} type="number" value={rent} onChange={(e) => setRent(+e.target.value || 0)} />
        </LabelBlock>
        <LabelBlock label={isZh ? "租期（月）" : "Lease (months)"}>
          <input style={inputCss} type="number" value={months} onChange={(e) => setMonths(+e.target.value || 0)} />
        </LabelBlock>
      </div>
      <div className="mt-3 flex items-baseline justify-between rounded-[10px] bg-surface-2 px-3 py-2.5">
        <div>
          <div className="text-[11px] text-text-muted">{isZh ? "整租期总租金" : "Total rent over term"}</div>
          <div style={{ fontFamily: "var(--font-serif)", fontVariantNumeric: "tabular-nums", fontSize: 16 }}>S${total.toLocaleString()}</div>
        </div>
        <div className="text-right">
          <div className="text-[11px] text-text-muted">{isZh ? "印花税" : "Stamp duty"}</div>
          <div style={{ fontFamily: "var(--font-serif)", fontVariantNumeric: "tabular-nums", fontSize: 22, fontWeight: 600, color: "var(--rent-accent)" }}>
            S${sd.toLocaleString()}
          </div>
        </div>
      </div>
      <button
        onClick={() => onAsk(isZh
          ? `如果我的 ${months} 个月、S$${rent}/月 租约续约，需要再交一次印花税吗？`
          : `What if my ${months}-month lease at S$${rent}/mo gets renewed — do I pay stamp duty again?`)}
        className="mt-2.5 w-full rounded-md border border-border-subtle px-3 py-1.5 text-[12px] text-text-sec transition hover:border-border-strong hover:text-text"
      >
        💬 {isZh ? "用 AI 追问一下" : "Ask AI a follow-up"}
      </button>
    </ToolCard>
  );
}

function ToolDeposit({ isZh, onAsk }: { isZh: boolean; onAsk: (q: string) => void }) {
  const [rent, setRent] = useState(3500);
  const [months, setMonths] = useState(12);
  const dep = rent * (months >= 24 ? 2 : 1);
  const advance = rent;
  const upfront = dep + advance;
  return (
    <ToolCard
      icon={<svg width="14" height="14" viewBox="0 0 14 14" fill="none"><circle cx="4" cy="10" r="2.2" stroke="currentColor" strokeWidth="1.3" /><path d="M5.5 8.5l4-4M8 6l1.5 1.5M9.5 4.5l1.5 1.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" /></svg>}
      title={isZh ? "签约日现金清单" : "Move-in cash check"}
      sub={isZh ? "签字当天要打的总额。" : "What you'll wire on the day you sign."}
    >
      <div className="grid grid-cols-2 gap-2.5">
        <LabelBlock label={isZh ? "月租" : "Monthly rent"}>
          <input style={inputCss} type="number" value={rent} onChange={(e) => setRent(+e.target.value || 0)} />
        </LabelBlock>
        <LabelBlock label={isZh ? "租期（月）" : "Lease (months)"}>
          <input style={inputCss} type="number" value={months} onChange={(e) => setMonths(+e.target.value || 0)} />
        </LabelBlock>
      </div>
      <div className="mt-3 rounded-[10px] border bg-surface-2 px-3 py-2.5 text-[12.5px]" style={{ borderColor: "var(--rule)" }}>
        <Row k={isZh ? `押金（${months >= 24 ? "2 个月" : "1 个月"}）` : `Security deposit (${months >= 24 ? "2 mo" : "1 mo"})`} v={`S$${dep.toLocaleString()}`} />
        <Row k={isZh ? "首月预付租金" : "Advance rent (1st month)"} v={`S$${advance.toLocaleString()}`} />
        <div className="mt-1 flex justify-between border-t border-dashed border-border pt-2">
          <span className="font-semibold">{isZh ? "签约日付总额" : "Upfront on signing"}</span>
          <span style={{ fontFamily: "var(--font-serif)", fontVariantNumeric: "tabular-nums", color: "var(--rent-accent)", fontSize: 16, fontWeight: 600 }}>
            S${upfront.toLocaleString()}
          </span>
        </div>
      </div>
      <button
        onClick={() => onAsk(isZh ? "可以谈成半个月押金吗？" : "Can I negotiate a half-month deposit instead?")}
        className="mt-2.5 w-full rounded-md border border-border-subtle px-3 py-1.5 text-[12px] text-text-sec transition hover:border-border-strong hover:text-text"
      >
        💬 {isZh ? "让 AI 帮我谈" : "Ask AI to negotiate this"}
      </button>
    </ToolCard>
  );
}

function ToolFairness({ isZh, onAsk }: { isZh: boolean; onAsk: (q: string) => void }) {
  const [rent, setRent] = useState(4800);
  const [bedrooms, setBedrooms] = useState("2");
  const [district, setDistrict] = useState("D9");
  // Live stats from the backend /rent/median endpoint. Falls back to
  // last-known-good while a new fetch is in flight so the bar doesn't
  // snap to 0. sample_size=0 → "not enough data".
  const [stats, setStats] = useState<{ median: number; p25: number; p75: number; sample_size: number } | null>(null);
  useEffect(() => {
    let cancelled = false;
    const url = `/rent/median?district=${encodeURIComponent(district)}&beds=${encodeURIComponent(bedrooms)}`;
    // apiJson resolves the API base from runtime config, so this works
    // in prod (api.algonavis.com) and dev (vite proxy) without
    // conditionals. A raw `fetch(url)` would land on the SPA fallback
    // in prod and parse the index.html as JSON.
    apiJson<{ median?: number; p25?: number; p75?: number; sample_size?: number }>(url)
      .then((j) => {
        if (cancelled || !j) return;
        setStats({
          median: typeof j.median === "number" ? j.median : 0,
          p25: typeof j.p25 === "number" ? j.p25 : 0,
          p75: typeof j.p75 === "number" ? j.p75 : 0,
          sample_size: typeof j.sample_size === "number" ? j.sample_size : 0,
        });
      })
      .catch(() => { /* keep previous stats — silent */ });
    return () => { cancelled = true; };
  }, [district, bedrooms]);
  const med = stats?.median && stats.median > 0 ? stats.median : 0;
  const sample = stats?.sample_size ?? 0;
  const ratio = med > 0 ? rent / med : 1;
  const pct = Math.max(2, Math.min(98, Math.round(50 + Math.log(ratio) * 80)));
  const verdict = pct < 35
    ? (isZh ? "低于市场" : "under market")
    : pct < 65
    ? (isZh ? "接近市场" : "about market")
    : (isZh ? "高于市场" : "above market");
  const verdictColor = pct < 35 ? "var(--pos)" : pct < 65 ? "var(--rent-accent)" : "var(--neg)";
  return (
    <ToolCard
      icon={<svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2 11l3-3 2.5 2.5L12 4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" /></svg>}
      title={isZh ? "公允租金速查" : "Fair rent quick-check"}
      sub={isZh ? "URA · 过去 12 个月 · 按区" : "URA leases · last 12 mo · per district"}
    >
      <div className="grid grid-cols-3 gap-2.5">
        <LabelBlock label={isZh ? "区" : "District"}>
          <select style={inputCss} value={district} onChange={(e) => setDistrict(e.target.value)}>
            {["D1", "D3", "D5", "D8", "D9", "D10", "D11", "D15"].map((d) => <option key={d}>{d}</option>)}
          </select>
        </LabelBlock>
        <LabelBlock label={isZh ? "卧室数" : "Bedrooms"}>
          <select style={inputCss} value={bedrooms} onChange={(e) => setBedrooms(e.target.value)}>
            <option>1</option><option>2</option><option>3</option>
          </select>
        </LabelBlock>
        <LabelBlock label={isZh ? "要价" : "Asking rent"}>
          <input style={inputCss} type="number" value={rent} onChange={(e) => setRent(+e.target.value || 0)} />
        </LabelBlock>
      </div>
      <div className="mt-3.5">
        <div className="relative h-2 rounded-full" style={{ background: "var(--color-surface-3)" }}>
          <div
            className="absolute left-0 top-0 bottom-0 rounded-full"
            style={{
              width: `${pct}%`,
              background: "linear-gradient(90deg, var(--pos), var(--rent-accent), var(--neg))",
            }}
          />
          <div
            className="absolute h-3.5 w-3.5 rounded-full border-2 bg-surface"
            style={{ top: -3, left: `calc(${pct}% - 7px)`, borderColor: verdictColor }}
          />
        </div>
        <div className="mt-1 flex justify-between text-[10.5px] text-text-muted">
          <span>{isZh ? "便宜" : "cheap"}</span>
          <span>{isZh ? "中位" : "median"}</span>
          <span>{isZh ? "溢价" : "premium"}</span>
        </div>
      </div>
      <div className="mt-3 flex items-baseline justify-between text-[12.5px] text-text-sec">
        <div>
          {med > 0 ? (
            isZh ? <>位于 <strong style={{ color: verdictColor }}>p{pct}</strong> · {verdict} · 中位 S${med.toLocaleString()} · n={sample}</> :
              <>Sits at <strong style={{ color: verdictColor }}>p{pct}</strong> · {verdict} · median S${med.toLocaleString()} · n={sample}</>
          ) : (
            <span style={{ color: "var(--text-muted)" }}>
              {isZh ? "该区/户型过去 12 个月成交样本不足" : "Not enough URA leases for this district + size in the last 12 mo"}
            </span>
          )}
        </div>
      </div>
      <button
        onClick={() => onAsk(isZh
          ? `${district} 的 ${bedrooms} 房 S$${rent} 合理吗？谈价技巧？`
          : `Is S$${rent} fair for a ${bedrooms}BR in ${district}? Negotiation tactics?`)}
        className="mt-2.5 w-full rounded-md border border-border-subtle px-3 py-1.5 text-[12px] text-text-sec transition hover:border-border-strong hover:text-text"
      >
        💬 {isZh ? "问 AI 拿建议" : "Ask AI"}
      </button>
    </ToolCard>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex justify-between py-[3px]">
      <span className="text-text-muted">{k}</span>
      <span style={{ fontFamily: "var(--font-serif)", fontVariantNumeric: "tabular-nums" }}>{v}</span>
    </div>
  );
}

/* Checklist tray ──────────────────────────────────────── */

const CHECKLIST_GROUPS_EN: { g: string; items: [string, string | null][] }[] = [
  { g: "Identity & paperwork", items: [
    ["Owner's NRIC matches title deed (ask agent for screenshot of SLA search)", "Why does this matter?"],
    ["Agent's CEA registration number is visible on the agreement",              "How do I verify a CEA number?"],
    ["Tenancy Agreement (TA) drafted in your name as registered on passport / NRIC", null],
  ]},
  { g: "Money", items: [
    ["Stamp duty paid by tenant within 14 days of signing — keep the e-Stamp certificate", "Walk me through e-Stamping"],
    ["Security deposit refund window stated (14 days post-handover is standard)", null],
    ["Letter of Intent (LOI) good-faith deposit credited as 1st month, in writing", null],
  ]},
  { g: "Clauses to look for", items: [
    ["Diplomatic / early-termination clause — only if lease ≥ 18 mo", "Sample wording for diplomatic clause"],
    ["Reinstatement clause — fair wear & tear excluded explicitly", "What's 'fair wear & tear'?"],
    ["Minor repairs cap — usually S$150–S$200 per incident, tenant's responsibility", null],
    ["Subletting & Airbnb — explicitly addressed (most leases prohibit)", null],
  ]},
  { g: "Move-in checks", items: [
    ["Inventory list signed by both parties on handover day", null],
    ["Photos of every appliance + meter readings on Day 1, sent to landlord by email", null],
    ["Wi-Fi & utility accounts transferred or new accounts opened in tenant's name", null],
  ]},
];

const CHECKLIST_GROUPS_ZH: { g: string; items: [string, string | null][] }[] = [
  { g: "身份与文件", items: [
    ["房东 NRIC 与产权证一致（请经纪给 SLA 查询截图）", "为什么这点重要？"],
    ["合同上能看到经纪人 CEA 注册号", "怎么验证 CEA 号？"],
    ["租约（TA）用你护照 / NRIC 上的姓名拟定", null],
  ]},
  { g: "钱", items: [
    ["租客在签约后 14 天内交印花税——保留 e-Stamp 证明", "带我走一遍 e-Stamping 流程"],
    ["押金退还时限写明（交房后 14 天是标准）", null],
    ["LOI 诚意金书面约定抵作首月租金", null],
  ]},
  { g: "要看的条款", items: [
    ["外交 / 提前解约条款——只在 ≥ 18 个月租约里", "外交条款的标准措辞"],
    ["复原条款——明确排除合理磨损", "什么算「合理磨损」？"],
    ["小维修上限——通常每次 S$150–S$200，租客承担", null],
    ["转租与 Airbnb——明确写明（多数租约禁止）", null],
  ]},
  { g: "入住检查", items: [
    ["交房当天双方签字确认家具清单", null],
    ["首日拍每件电器与水电气表读数，邮件发给房东", null],
    ["Wi-Fi 和水电账号过户或以租客名义新开", null],
  ]},
];

function ChecklistTray({ isZh, onClose, onAsk }: { isZh: boolean; onClose: () => void; onAsk: (q: string) => void }) {
  const [done, setDone] = useState<Record<string, boolean>>({});
  const groups = isZh ? CHECKLIST_GROUPS_ZH : CHECKLIST_GROUPS_EN;
  const total = groups.reduce((n, g) => n + g.items.length, 0);
  const doneN = Object.values(done).filter(Boolean).length;
  return (
    <TrayShell
      title={isZh ? "签字之前" : "Before you sign"}
      subtitle={isZh ? `${doneN} / ${total} 已确认 · 进度只存在本设备` : `${doneN} of ${total} done · save progress on this device`}
      onClose={onClose}
      wide
    >
      <div className="px-5 pt-2 pb-3">
        <div className="h-1.5 overflow-hidden rounded-full" style={{ background: "var(--color-surface-3)" }}>
          <div
            className="h-full transition-[width] duration-300"
            style={{ width: `${total > 0 ? (doneN / total) * 100 : 0}%`, background: "var(--rent-accent)" }}
          />
        </div>
      </div>
      {groups.map((g, gi) => (
        <div key={gi} className="px-5 pb-4 pt-1">
          <div className="mb-2 mt-1 text-[10.5px] font-semibold uppercase tracking-[0.08em] text-text-muted">
            {g.g}
          </div>
          {g.items.map(([t, ask], i) => {
            const key = `${gi}-${i}`;
            const isDone = !!done[key];
            return (
              <div key={i} className="flex items-start gap-2.5 py-1.5">
                <button
                  onClick={() => setDone((d) => ({ ...d, [key]: !d[key] }))}
                  className="mt-0.5 flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-[4px] border-2 transition"
                  style={{
                    borderColor: isDone ? "var(--rent-accent)" : "var(--rule-strong)",
                    background: isDone ? "var(--rent-accent)" : "transparent",
                    color: "var(--rent-accent-on)",
                  }}
                  aria-label="Toggle"
                >
                  {isDone && <svg width="10" height="10" viewBox="0 0 12 12" fill="none"><path d="M2 6.5l2.5 2.5L10 3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>}
                </button>
                <div className="min-w-0 flex-1">
                  <div
                    className="text-[13px] leading-[1.45]"
                    style={{
                      color: isDone ? "var(--text-muted)" : "var(--text)",
                      textDecoration: isDone ? "line-through" : "none",
                    }}
                  >
                    {t}
                  </div>
                  {ask && (
                    <button
                      onClick={() => onAsk(ask)}
                      className="mt-1 flex items-center gap-1 text-[11.5px] font-medium"
                      style={{ color: "var(--rent-accent)" }}
                    >
                      ✦ {ask}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ))}
    </TrayShell>
  );
}


// LEAD_KEY namespaces the rent-side capture flag separately from the
// buyer flow's `propwise.lead_submitted` — a user who already left a
// buy-side lead may have a fresh rent need we still want to route, and
// vice versa. submission flips this to "1" and the card stays hidden
// for that browser thereafter.
const RENT_LEAD_KEY = STORAGE_LEADS.leadSubmittedRent;

function RentLeadCard({
  isZh, brief, source = LEAD_SOURCE.rentChat, variant = "generic",
}: {
  isZh: boolean;
  brief: RentBrief | null;
  // source tags the /leads/capture write so the pro inbox can tell a
  // commute-moment lead (rent_commute) from a generic after-chat lead
  // (rent-chat). Both land unassigned in the same claim pool.
  source?: LeadSource;
  // variant only swaps the headline/sub copy — the form + POST are
  // identical. "commute" is shown right after a transit_commute_estimate
  // answer; "generic" is the ≥2-reply fallback.
  variant?: "generic" | "commute";
}) {
  const { t } = useTranslation();
  const initialDismissed = typeof window !== "undefined" && !!localStorage.getItem(RENT_LEAD_KEY);
  const [dismissed, setDismissed] = useState(initialDismissed);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState(""); // WhatsApp number
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(false);
  if (dismissed) return null;

  const budgetSGD: number | undefined =
    brief?.priceMax ?? brief?.priceMin ?? undefined;
  const districtLabel = brief?.district || brief?.areaLabel || "";
  const briefSummary = brief
    ? [
        brief.priceMin || brief.priceMax
          ? `S$${brief.priceMin ?? "?"}${brief.priceMax ? "–" + brief.priceMax : ""}`
          : null,
        brief.areaLabel || brief.district,
        brief.bedrooms != null ? bedLabel(brief.bedrooms, isZh) : null,
        brief.moveIn ? (isZh ? `入住 ${brief.moveIn}` : `move-in ${brief.moveIn}`) : null,
      ]
        .filter(Boolean)
        .join(" · ")
    : "";

  async function submit() {
    const cleanName = name.trim();
    const cleanPhone = phone.trim();
    if (!cleanName || !cleanPhone) return;
    setSending(true);
    try {
      await apiPostJson("/leads/capture", {
        name: cleanName,
        phone: cleanPhone,
        source,
        property_type: "rent",
        district: districtLabel || undefined,
        budget_sgd: budgetSGD ? Math.round(budgetSGD) : undefined,
        context: briefSummary || (isZh ? "新加坡租房咨询" : "Singapore rental enquiry"),
      });
    } catch {
      // Silent fail — don't block the user; they can still reach an
      // agent through other surfaces.
    }
    if (typeof window !== "undefined") localStorage.setItem(RENT_LEAD_KEY, "1");
    setSuccess(true);
    setSending(false);
  }

  return (
    <div
      className="rent-msg-in relative my-4 rounded-[14px] border p-4 sm:p-5"
      style={{
        background: "var(--rent-accent-dim)",
        borderColor: "var(--rent-accent)",
      }}
    >
      <button
        aria-label={isZh ? "关闭" : "Dismiss"}
        onClick={() => setDismissed(true)}
        className="absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-md text-text-muted hover:text-text"
      >
        ×
      </button>
      <div className="flex gap-3">
        <div className="text-xl">🤝</div>
        <div className="min-w-0 flex-1">
          <div className="text-[14px] font-semibold text-text">
            {variant === "commute"
              ? t("leadCta.rentCommute.headline")
              : (isZh ? "让持牌经纪人帮你找匹配房源？" : "Get matched with a licensed agent?")}
          </div>
          <div className="mt-1 text-[12.5px] text-text-sec">
            {variant === "commute"
              ? t("leadCta.rentCommute.sub")
              : (isZh
                ? "留个 WhatsApp，CEA 持牌经纪人有合适的真实房源会主动推给你（PG / 99.co 上看不到的私房源也可能进来）。不会变成骚扰电话清单。"
                : "Drop your WhatsApp — a CEA-licensed agent will reach out when a matching listing comes up (incl. off-market ones not on PG / 99.co). Not a robo-call list.")}
          </div>
          {briefSummary && (
            <div className="mt-2 inline-flex items-center gap-1 rounded-md border border-border-subtle bg-surface px-2 py-[3px] text-[11.5px] text-text-sec">
              {isZh ? "经纪人会看到：" : "Agent will see: "}{briefSummary}
            </div>
          )}
          {success ? (
            <div className="mt-3 text-[13px] font-medium" style={{ color: "var(--pos)" }}>
              {isZh ? "✓ 已发送 — 持牌经纪人 1-2 天内 WhatsApp 联系你。" : "✓ Sent — a licensed agent will WhatsApp you within 1-2 days."}
            </div>
          ) : (
            <div className="mt-3 flex flex-col gap-2 sm:flex-row">
              <input
                type="text"
                className="input-field flex-1"
                placeholder={isZh ? "称呼" : "Your name"}
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
              <input
                type="tel"
                inputMode="tel"
                className="input-field flex-1"
                placeholder={isZh ? "WhatsApp 号码（含国家码）" : "WhatsApp number (with country code)"}
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
              <button
                type="button"
                onClick={submit}
                disabled={sending || !name.trim() || !phone.trim()}
                className="rounded-lg px-4 py-2 text-[13px] font-semibold transition disabled:cursor-not-allowed disabled:opacity-50"
                style={{ background: "var(--rent-accent)", color: "var(--rent-accent-on)" }}
              >
                {sending ? (isZh ? "发送中…" : "Sending…") : (isZh ? "发送" : "Send")}
              </button>
            </div>
          )}
          <div className="mt-2 text-[11px] text-text-muted">
            {isZh
              ? "提交即同意将以上联系方式 + 租房需求转给一位持牌经纪人。可随时回信「停止」。"
              : "Submitting shares your number + brief with one licensed agent. Reply STOP anytime."}
          </div>
        </div>
      </div>
    </div>
  );
}


function bedLabel(b: number | undefined, isZh: boolean): string {
  if (b == null) return isZh ? "未指定" : "any";
  if (b === 0) return isZh ? "开间" : "studio";
  return isZh ? `${b} 房` : `${b}BR`;
}
